import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Reveal, SectionHeading } from "./common";
import { TIMELINE } from "../data/content";

const FILTERS = [
  { key: "all", label: "Tutte le epoche" },
  { key: "XIV", label: "XIV secolo" },
  { key: "XV", label: "XV secolo" },
  { key: "XIX-XX", label: "XIX–XX secolo" },
];

const matches = (item, filter) => {
  if (filter === "all") return true;
  if (filter === "XIX-XX") return item.century === "XIX" || item.century === "XX";
  return item.century === filter;
};

export const Storia = () => {
  const [filter, setFilter] = useState("all");
  const [openIdx, setOpenIdx] = useState(null);
  const items = TIMELINE.filter((t) => matches(t, filter));

  return (
    <section id="storia" className="relative bg-sand py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Capitolo I" title="Storia" testid="section-storia-title" />
            <Reveal delay={0.15}>
              <div className="mt-8 space-y-5 text-base sm:text-lg text-taupe leading-relaxed">
                <p>
                  La storia della Basilica comincia con{" "}
                  <strong className="text-espresso font-semibold">
                    Raimondello Orsini del Balzo
                  </strong>
                  , conte di Soleto e una delle figure più potenti del Regno di
                  Napoli del tardo Trecento. È a lui che la tradizione
                  storiografica lega la fondazione della chiesa, collocandone la
                  costruzione{" "}
                  <strong className="text-espresso font-semibold">
                    tra il 1369 e il 1391
                  </strong>
                  : date tramandate dalla tradizione, che gli studiosi trattano
                  con prudenza perché la documentazione superstite non consente
                  certezze assolute.
                </p>
                <p>
                  Alla morte di Raimondello (1406), la sua eredità fu raccolta
                  dalla moglie{" "}
                  <strong className="text-espresso font-semibold">
                    Maria d'Enghien
                  </strong>
                  , contessa di Lecce, e poi dal figlio{" "}
                  <strong className="text-espresso font-semibold">
                    Giovanni Antonio Orsini del Balzo
                  </strong>
                  , principe di Taranto: sotto la loro committenza, nel corso
                  del Quattrocento, l'interno della chiesa si coprì di affreschi.
                </p>
                <p>
                  La chiesa fu affidata ai{" "}
                  <strong className="text-espresso font-semibold">
                    Frati Minori Francescani
                  </strong>
                  , che ancora oggi custodiscono e animano questo luogo, elevato
                  a basilica minore nel 1992.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2 mb-10" role="group" aria-label="Filtro per secolo">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => {
                      setFilter(f.key);
                      setOpenIdx(null);
                    }}
                    data-testid={`timeline-filter-${f.key.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                    className={`font-mono text-xs tracking-widest uppercase px-4 py-2 border transition-all duration-300 rounded-full ${
                      filter === f.key
                        ? "bg-espresso text-ivory border-espresso"
                        : "bg-transparent text-taupe border-stone hover:border-gold hover:text-espresso"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </Reveal>

            <div className="relative pl-8 sm:pl-10">
              <div
                className="absolute left-[9px] sm:left-[11px] top-2 bottom-2 w-px bg-gradient-to-b from-gold via-stone to-transparent"
                aria-hidden="true"
              />
              <AnimatePresence mode="popLayout">
                {items.map((item, i) => {
                  const idx = TIMELINE.indexOf(item);
                  const open = openIdx === idx;
                  return (
                    <motion.article
                      key={item.year}
                      layout
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                      className="relative mb-6 last:mb-0"
                    >
                      <span
                        className={`absolute -left-8 sm:-left-10 top-6 w-[19px] h-[19px] sm:w-[23px] sm:h-[23px] rounded-full border-2 transition-colors duration-300 ${
                          open ? "bg-gold border-gold" : "bg-sand border-gold/60"
                        }`}
                        aria-hidden="true"
                      />
                      <button
                        onClick={() => setOpenIdx(open ? null : idx)}
                        data-testid={`timeline-item-${item.year.replace(/[^0-9a-z]/gi, "")}`}
                        aria-expanded={open}
                        className={`w-full text-left bg-ivory border p-5 sm:p-6 transition-all duration-300 group ${
                          open
                            ? "border-gold shadow-[0_14px_40px_rgba(43,35,31,0.10)]"
                            : "border-stone hover:border-gold/60 hover:shadow-[0_10px_30px_rgba(43,35,31,0.06)]"
                        }`}
                      >
                        <div className="flex items-baseline justify-between gap-4">
                          <span className="font-serif text-2xl sm:text-3xl font-bold text-gold">
                            {item.year}
                          </span>
                          <ChevronDown
                            size={18}
                            className={`text-fog transition-transform duration-300 flex-shrink-0 ${
                              open ? "rotate-180 text-gold" : "group-hover:text-espresso"
                            }`}
                          />
                        </div>
                        <h3 className="mt-1 font-serif text-lg sm:text-xl font-semibold text-espresso">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm sm:text-base text-taupe leading-relaxed">
                          {item.short}
                        </p>
                        <AnimatePresence initial={false}>
                          {open && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                              className="overflow-hidden"
                            >
                              <p className="pt-4 mt-4 border-t border-stone text-sm sm:text-base text-espresso/80 leading-relaxed">
                                {item.detail}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </button>
                    </motion.article>
                  );
                })}
              </AnimatePresence>
            </div>
            <Reveal delay={0.1}>
              <p className="mt-6 text-xs sm:text-sm text-fog font-mono tracking-wide">
                Tocca una tappa per approfondire · Le date di fondazione sono
                riportate secondo la tradizione storiografica
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
